import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import { ActionConfirmDialog } from '@/apps/latest/components/molecules/ActionConfirmDialog'
import AudioPlayer from '@/apps/latest/components/molecules/AudioPlayer'
import { RecordingFailedAlert } from '@/apps/latest/components/molecules/RecordingFailedAlert'
import { Layout } from '@/apps/latest/components/templates/Layout'
import { useIsUploadingAttachments } from '@/apps/latest/features/attachments/attachmentUploads'
import { useRecordingImages } from '@/apps/latest/features/attachments/useRecordingImages'
import { NutritionFeedbackAction } from '@/apps/latest/features/nutrition/NutritionFeedbackAction'
import { NutritionMaterials } from '@/apps/latest/features/nutrition/NutritionMaterials'
import { NutritionRecordingBar } from '@/apps/latest/features/nutrition/NutritionRecordingBar'
import { NutritionRegenerateButton } from '@/apps/latest/features/nutrition/NutritionRegenerateButton'
import { NutritionRecordRounds } from '@/apps/latest/features/nutrition/NutritionRecordRounds'
import { NutritionStaffMenu } from '@/apps/latest/features/nutrition/NutritionStaffMenu'
import {
  nutritionRoundsKey,
  useNutritionCustomer,
  useNutritionRounds,
  type RoundStartedBy,
} from '@/apps/latest/features/nutrition/useNutritionCustomers'
import {
  acceptsMaterials,
  deriveNutritionPhase,
  type NutritionPhase,
} from '@/apps/latest/features/nutrition/nutritionPhase'
import { renderNutritionSidebar } from '@/apps/latest/features/nutrition/nutritionShell'
import {
  useCommitNutritionAudio,
  useDiscardNutritionAudio,
  useGenerateNutritionReport,
  useNutritionReport,
  useRetranscribeNutrition,
} from '@/apps/latest/features/nutrition/useNutritionReport'
import { useRecordingClock } from '@/apps/latest/features/nutrition/useRecordingClock'
import type { MemoFlush } from '@/apps/latest/features/yakureki/components/MemoPanel'
import { useActiveRecordingEmit } from '@/apps/latest/shared/recording-status/useActiveRecordingEmit'
import { usePollEvent } from '@/apps/latest/shared/recording-status/usePollEvent'
import { useRecordingStatus } from '@/apps/latest/shared/recording-status/useRecordingStatus'
import { NOTION_BUTTON_PRIMARY_SX } from '@/shared/styles/buttonStyles'

// 48 kbps の AAC で 90 分 ≈ 32 MB (前段の nginx は 50M。既定の約 128 kbps だと 90 分で約 86 MB)。
const AUDIO_BITS_PER_SECOND = 48_000
// これ以下で止めたら確認を挟む。共有 engine が toast を出す短録音の長さ (features/yakureki/recording/audioPolicy の
// RECORDING_MIN_S = back の upload 経路が弾く長さ) と同じ 10 秒。この系統の back は弾かないので、画面で止める。
const SHORT_RECORDING_S = 10

// 名前の脇に出す状態の 1 行。
const CAPTION: Partial<Record<NutritionPhase, string>> = {
  transcribing: '文字起こし中…',
  generating: '報告書を作成中…',
  transcribeFailed: '文字起こしに失敗しました',
  generateFailed: '報告書を作れませんでした',
}

interface Props {
  // 今回の記録 (録音イベント)。カルテ (pages/nutrition/customer) が back の今回の印か、最初の 1 手で始めた記録を渡す
  eventId: string
  customerId: string
  // 今回を始めた手。録る = 今回ができた時点で録音を始める (同じ 1 手)・メモ = メモ欄に focus を置く。1 回だけ読む
  startedBy: RoundStartedBy | null
  onStartedByUsed: () => void
  // 今回が消えた (記録を削除・他の端末で消された)。カルテは今回の無い形に戻る
  onGone: () => void
}

// 今回のあるカルテ (design-book ADR-0084・ADR-0086)。記録はカルテの最初の 1 手 (録る・資料を足す・メモを書く) と
// 登録で生まれ (PENDING の器)、この 1 枚で 録る → 文字起こし → 作る → 完了 を持つ。URL はカルテの 1 つのまま。
// 段階は back が決めた記録の段 (report.stage) と録音の engine から deriveNutritionPhase が決める。
// 録音は batch だけ: 録っている間はサーバを呼ばず、止めたら全音声を 1 回送る (系統の audio/)。
// 作るは管理栄養士の手 (自動では作らない)。作る = 作り直す。
// 共有カーネルから借りるのは engine (useRecordingStatus)・polling・退避・左ペインの録音中表示と、
// 添付・メモ・音声の再生の部品だけ。MeetingBlock・一時停止・破棄は使わない。
// 送った後の録音は名前の行のすぐ下で聞き返せる (録音の操作が居た場所に、送った録音の再生が来る)。
// 報告書の置き場は「回」を選ぶ行と 報告書 / 栄養価 / 資料 / 会話 のタブ (NutritionRecordRounds。ADR-0082・ADR-0085)。
// 2 回目以降は、今回の報告書が着地するまで前回を出し、回をさかのぼれる。着地したら今回に替わる。
// 名前の行はお客様 (マスタ) を読み、右端は今回の手 (作る・もう一度・作り直す) だけ。出す と 記録を削除 は回の行
// (選んだ回に効く。ADR-0086 決定 7)。お客様の情報は左ペインの行の ⋯ から。
// 完了した記録のページ下部に二次アクションの行 (改善依頼。ADR-0043 の形をそのまま)。
export const NutritionKarteCurrent = ({
  eventId,
  customerId,
  startedBy,
  onStartedByUsed,
  onGone,
}: Props) => {
  const yakurekiId = eventId

  const { data: event, isGone } = usePollEvent({ yakurekiId })
  const { data: report } = useNutritionReport({
    eventId: yakurekiId,
    eventStatus: event?.status,
  })
  const { data: customer } = useNutritionCustomer(customerId)
  const { data: rounds } = useNutritionRounds(customerId)
  const hasOtherRounds = (rounds ?? []).some((r) => r.id !== yakurekiId)
  // 始めたばかりの今回は中身の無い器で、back は回の一覧にも左ペインの並び (最後に記録を始めた日) にも数えない。
  // メモ・資料・音声のどれかが入った時点で両方を取り直す (始めた直後の取り直しでは、まだ器のまま)。
  const queryClient = useQueryClient()
  const { list: images } = useRecordingImages(yakurekiId)
  const listed = (rounds ?? []).some((r) => r.id === yakurekiId)
  const filled =
    (event?.memo_items?.length ?? 0) > 0 ||
    (images.data?.length ?? 0) > 0 ||
    (!!event && event.status !== 'pending')
  useEffect(() => {
    if (!rounds || listed || !filled) return
    void queryClient.invalidateQueries({
      queryKey: nutritionRoundsKey(customerId),
    })
    void queryClient.invalidateQueries({ queryKey: ['nutritionCustomers'] })
  }, [rounds, listed, filled, customerId, queryClient])

  const commitAudio = useCommitNutritionAudio(yakurekiId)
  // 録音の秒数は engine の状態から数えるので、engine に渡す送り先からは ref 越しに読む。
  const durationRef = useRef<() => number>(() => 0)
  // 止めたときの全音声の送り先。失敗は engine が控えを握り、再送 (retry) でまたここに来る。
  const onFallbackCommit = useCallback(
    (blob: Blob) => commitAudio(blob, durationRef.current()),
    [commitAudio]
  )
  const recordingStatus = useRecordingStatus({ event, onFallbackCommit })
  const { startRecording, stopRecording, engine } = recordingStatus
  useActiveRecordingEmit(recordingStatus)
  useEffect(() => {
    if (isGone) onGone()
  }, [isGone, onGone])

  const { elapsed, duration } = useRecordingClock(
    recordingStatus.status === 'recording'
  )
  useEffect(() => {
    durationRef.current = duration
  }, [duration])
  // 取り消せない手の確認 (録り直す・短い録音を止める)
  const [confirm, setConfirm] = useState<'restart' | 'shortStop' | null>(null)

  // engine の terminal cleanup (薬剤師の page と同じ)。
  useEffect(() => {
    if (engine.state === 'completed') {
      engine.reset()
      return
    }
    if (engine.state === 'failed' && !engine.hasPendingBlob) {
      engine.reset()
    }
  }, [engine.state, engine.hasPendingBlob, engine])

  const handleStart = useCallback(() => {
    startRecording({
      yakurekiIds: [yakurekiId],
      route: 'batch',
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    })
  }, [yakurekiId, startRecording])
  // 録り直す = 録音を捨てて記録を録音前に戻してから、マイクを起動する (以降は初回と同じ道)。
  // 完了・失敗の記録のままマイクを起動すると、共有の録音状態 (マイクが動くのは録音前の記録だけ) と
  // back (1 録音 = 1 RecordingSession) の前提を破る。捨てられなければ (409 など) マイクは起動しない。
  const discard = useDiscardNutritionAudio(yakurekiId)
  const { mutateAsync: discardAudio } = discard
  const handleRestart = useCallback(async () => {
    try {
      await discardAudio()
    } catch {
      return // toast は hook が出す
    }
    handleStart()
  }, [discardAudio, handleStart])
  // 相談の冒頭で ⇧Enter や ■ を押してしまう手を止める。10 秒以下 (back の upload 経路が弾く長さ) は確認を挟む
  // (共有の stopRecording は toast を出すだけで止める)。
  const handleStop = useCallback(() => {
    if (duration() <= SHORT_RECORDING_S) {
      setConfirm('shortStop')
      return
    }
    stopRecording()
  }, [duration, stopRecording])

  const memoFlushRef = useRef<MemoFlush | null>(null)
  const generate = useGenerateNutritionReport(yakurekiId)
  const retranscribe = useRetranscribeNutrition(yakurekiId)
  // 作るはその時点のメモと資料を読む。打ちかけのメモは保存を待ってから作り、送っている最中の資料があれば
  // 作る・作り直すを押せなくする (送り終われば押せる)。
  const uploadingAttachments = useIsUploadingAttachments(yakurekiId)
  const uploadingReason = uploadingAttachments
    ? '資料を送っています。送り終わると作れます'
    : undefined
  const handleGenerate = useCallback(async () => {
    await memoFlushRef.current?.()
    generate.mutate()
  }, [generate])

  const phase = event
    ? deriveNutritionPhase(recordingStatus, report)
    : 'loading'
  // カルテで ● を押して今回ができたら、その時点で録音を始める (同じ 1 手)。1 回だけで、始めた手はカルテに返す
  const [memoAutoFocus] = useState(startedBy === 'memo')
  useEffect(() => {
    if (startedBy === 'memo') onStartedByUsed()
    if (startedBy !== 'record' || phase !== 'before') return
    onStartedByUsed()
    handleStart()
  }, [startedBy, phase, handleStart, onStartedByUsed])
  // 送った後に録り直せる段階 (作る前・失敗)。録り直すは確認の後に handleRestart (捨ててからマイクを起動)
  const canRestart =
    phase === 'ready' ||
    phase === 'transcribeFailed' ||
    phase === 'generateFailed'
  const caption =
    recordingStatus.status === 'stopping'
      ? '音声を送っています…'
      : report?.stage === 'regenerate_failed'
      ? '作り直せませんでした'
      : CAPTION[phase]

  const phaseSlot: ReactNode =
    phase === 'ready' || phase === 'generateFailed' ? (
      // 押せない間はその理由を 1 行 (資料を送っている間)
      <Tooltip title={uploadingReason ?? ''}>
        <span>
          <Button
            variant="contained"
            size="small"
            sx={NOTION_BUTTON_PRIMARY_SX}
            onClick={() => void handleGenerate()}
            disabled={
              generate.isPending || discard.isPending || uploadingAttachments
            }
          >
            {phase === 'ready' ? '報告書を作る' : 'もう一度作る'}
          </Button>
        </span>
      </Tooltip>
    ) : phase === 'transcribeFailed' ? (
      <Button
        variant="contained"
        size="small"
        sx={NOTION_BUTTON_PRIMARY_SX}
        onClick={() => retranscribe.mutate()}
        disabled={retranscribe.isPending || discard.isPending}
      >
        もう一度文字起こす
      </Button>
    ) : phase === 'completed' ? (
      // 作り直すは名前の行の右端 (今回の手。出すは回の行)
      <NutritionRegenerateButton
        onConfirm={() => void handleGenerate()}
        disabled={generate.isPending}
        disabledReason={uploadingReason}
      />
    ) : undefined
  // 今回を録っている間・送っている間・作成中は、どの回にも記録を削除を出さない (消す前に止める。前の回を消すと、
  // 作っている今回が読む前回と指導回数が替わる)。往復中の手があるときも同じ
  const busy =
    phase === 'recording' ||
    recordingStatus.status === 'stopping' ||
    phase === 'generating' ||
    discard.isPending ||
    generate.isPending ||
    retranscribe.isPending
  // 送った後の録音 (サーバに置いた音声)。録音中は無い (録り直すと消える)
  const audioUrl = phase !== 'recording' ? event?.audio?.audio : undefined
  // 回の行は記録が 1 つでもあれば出す (今回の 出す・記録を削除 の置き場。ADR-0086 決定 7)。
  // 着地した後に作り直している間は出さない (今回の格子が戻るまでスピナー)
  const showRounds = Boolean(
    report &&
      phase !== 'loading' &&
      !(phase === 'generating' && report.generated_at)
  )
  // 前の回の格子を出している間は広く (格子は 720px だと欄が縦に割れる)
  const showPast = Boolean(report && !report.generated_at && hasOtherRounds)

  return (
    <Layout
      breadcrumb={['お客様', customer?.name ?? '']}
      renderSidebar={renderNutritionSidebar}
      // 完了後は様式と会話を横に並べる (design-book 04-apps/kakeru.md 「管理栄養士の報告書」)。
      wide={phase === 'completed' || showPast}
    >
      {!event ? null : (
        <Stack spacing={3}>
          <NutritionRecordingBar
            customer={customer}
            ageOn={report?.stamp.recorded_on}
            recording={
              phase === 'before' ||
              (phase === 'recording' && !recordingStatus.showRetryCta) ||
              canRestart
                ? {
                    status: recordingStatus.status,
                    stream: engine.stream,
                    elapsedSeconds: elapsed,
                    // 録り直しの往復中は確認を開かない (⇧Enter も onStart を呼ぶ)
                    onStart: canRestart
                      ? () => {
                          if (!discard.isPending) setConfirm('restart')
                        }
                      : handleStart,
                    onStop: handleStop,
                    restart: canRestart,
                    busy: discard.isPending,
                  }
                : undefined
            }
            caption={caption}
            // 担当 (署名の管理栄養士) は右の並びの左端。どの段でも出す (design-book ADR-0078)
            pillSlot={
              <NutritionStaffMenu
                eventId={event.id}
                value={event.pharmacist_id ?? null}
              />
            }
            rightSlot={phaseSlot}
          />
          {recordingStatus.showRetryCta ? (
            <RecordingFailedAlert
              errorMessage={recordingStatus.errorMessage ?? ''}
              hasPendingBlob
              isRetryHopeless={recordingStatus.isRetryHopeless}
              onRetry={() => void engine.retry()}
              onDownload={engine.downloadBlob}
              retrying={engine.state === 'finalizing'}
            />
          ) : (
            recordingStatus.errorMessage && (
              <Alert severity="error">{recordingStatus.errorMessage}</Alert>
            )
          )}
          {audioUrl && (
            // 音の大きさの推移 (1 秒ごと。back が文字起こしの前に測る) を再生バーの上に並べ、静かだった時間を見分ける
            <Box sx={{ maxWidth: 720 }}>
              <AudioPlayer
                key={`audioPlayer-${audioUrl}`}
                audioUrl={audioUrl}
                loudness={event?.audio?.loudness}
              />
            </Box>
          )}
          {acceptsMaterials(phase) && (
            <NutritionMaterials
              yakurekiId={yakurekiId}
              memoItems={event.memo_items ?? []}
              memoFlushRef={memoFlushRef}
              memoAutoFocus={memoAutoFocus}
            />
          )}
          {(phase === 'generating' || phase === 'loading') && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {report && showRounds && (
            // 回を選び、その回を 報告書 / 栄養価 / 資料 / 会話 のタブで出す。着地する前は前回、着地した後は今回から
            // (design-book ADR-0082・ADR-0086)
            <NutritionRecordRounds
              // 今回を替えたら選んでいた回とタブを持ち越さない
              key={`nutritionRecordRounds-${yakurekiId}`}
              customerId={customerId}
              current={{
                event,
                report,
                landed: phase === 'completed',
                memoFlushRef,
              }}
              busy={busy}
              onDeleted={(id) => {
                if (id === yakurekiId) onGone()
              }}
            />
          )}
          {/* ページ下部の二次アクションの行 (改善依頼)。薬剤師の録音後の画面と同じ置き場 */}
          {phase === 'completed' && (
            <NutritionFeedbackAction eventId={yakurekiId} />
          )}
          <ActionConfirmDialog
            open={confirm === 'restart'}
            title="録り直す"
            body="今の音声と文字起こしは捨てて、録り直します。添付資料とメモは残ります。"
            actionLabel="録り直す"
            onClose={() => setConfirm(null)}
            onConfirm={() => void handleRestart()}
          />
          <ActionConfirmDialog
            open={confirm === 'shortStop'}
            title="録音を止める"
            body={`録音が ${SHORT_RECORDING_S} 秒以下です。止めると、この短い音声のまま文字起こしに進みます。`}
            actionLabel="止める"
            onClose={() => setConfirm(null)}
            onConfirm={stopRecording}
          />
        </Stack>
      )}
    </Layout>
  )
}
